import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EventNoteIcon from "@mui/icons-material/EventNote";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { LineChart } from './LineChart';
import type { ChartEventMarker, ChartBand } from './LineChart';
import { mapDatesToAxis, mapRangeToAxis } from "./mapEventsToAxis";
import {
  useClientEvents,
  useCreateClientEvent,
  useDeleteClientEvent,
} from "../../hooks/useClientEvents";
import { useWeatherAnomalies } from "../../hooks/useMetrics";
import type { ClientEventCategory } from "../../api/clientEvents";
import type { MetricsFilters } from "../../api/types";

type LineChartProps = React.ComponentProps<typeof LineChart>;

interface AnnotatableChartProps extends Omit<LineChartProps, "events" | "onDateClick"> {
  /** Effective client scope; annotations are disabled when null (all-clients). */
  clientId: string | null;
  /** Visible window (YYYY-MM-DD) used to fetch events + anomalies. */
  startDate?: string;
  endDate?: string;
  /** Filters for the weather-anomaly query (same shape metrics use). */
  weatherFilters?: MetricsFilters;
  /** Whether the current admin may create/delete events. */
  canEdit?: boolean;
  /** Overlay abnormal-weather markers alongside manual events. Default true. */
  showWeatherAnomalies?: boolean;
}

const CATEGORY_COLORS: Record<ClientEventCategory, string> = {
  weather: "#E67E22",
  operational: "#4951B6",
  peak4_admin_notice: "#47A8AF", // PEAK4 brand teal
  other: "#A77DD2",
};

// peak4_admin_notice doesn't capitalize cleanly from its snake_case value, so
// every category gets an explicit label rather than relying on CSS capitalize.
const CATEGORY_LABELS: Record<ClientEventCategory, string> = {
  weather: "Weather",
  operational: "Operational",
  peak4_admin_notice: "PEAK4 Admin Notice",
  other: "Other",
};

/**
 * LineChart + client-event annotations. Renders manual client events and
 * abnormal-weather callouts as vertical markers, and (when canEdit) lets an
 * admin click an x-axis date to add an event for that client + date.
 */
export const AnnotatableChart: React.FC<AnnotatableChartProps> = ({
  clientId,
  startDate,
  endDate,
  weatherFilters,
  canEdit = false,
  showWeatherAnomalies = true,
  data,
  ...lineChartProps
}) => {
  const theme = useTheme();
  const chartKey = useMemo(() => Math.random().toString(36).slice(2, 9), []);

  const eventsQuery = useClientEvents({ clientId, startDate, endDate });
  const anomaliesQuery = useWeatherAnomalies({
    ...(weatherFilters ?? {}),
    client_id: clientId ?? undefined,
    start_date: startDate,
    end_date: endDate,
    enabled: showWeatherAnomalies,
  });

  const createEvent = useCreateClientEvent();
  const deleteEvent = useDeleteClientEvent(clientId);

  const events = eventsQuery.data?.data.events ?? [];
  const anomalies = anomaliesQuery.data?.data.anomalies ?? [];

  // Manual client events → vertical markers with a readable pill label.
  const markers: ChartEventMarker[] = useMemo(() => {
    const rows = data as Parameters<typeof mapDatesToAxis>[0];

    const eventMarkers = mapDatesToAxis(
      rows,
      events.map((e) => ({ ...e, date: e.eventDate })),
      (e, axisValue) => ({
        axisValue,
        label: e.title,
        color: CATEGORY_COLORS[e.category] ?? theme.palette.warning.main,
      }),
    );

    // Point-in-time weather anomalies (single-day) also render as markers.
    const pointAnomalyMarkers =
      showWeatherAnomalies
        ? mapDatesToAxis(
            rows,
            anomalies
              .filter((a) => a.startDay === a.endDay)
              .map((a) => ({ ...a, date: a.startDay })),
            (a, axisValue) => ({
              axisValue,
              label: a.isForecast ? `⚠ ${a.label}` : a.label,
              color:
                a.severity === "severe"
                  ? theme.palette.error.main
                  : theme.palette.warning.main,
            }),
          )
        : [];

    return [...pointAnomalyMarkers, ...eventMarkers];
  }, [data, events, anomalies, showWeatherAnomalies, theme]);

  // Multi-day weather anomalies (heatwaves, cold snaps) → shaded bands. Clamped
  // to the visible window, so a window fully inside a heatwave still shades.
  const bands: ChartBand[] = useMemo(() => {
    if (!showWeatherAnomalies) return [];
    const rows = data as Parameters<typeof mapRangeToAxis>[0];
    const out: ChartBand[] = [];
    for (const a of anomalies) {
      if (a.startDay === a.endDay) continue; // point anomalies handled as markers
      const span = mapRangeToAxis(rows, a.startDay, a.endDay);
      if (!span) continue;
      out.push({
        startAxisValue: span.startAxisValue,
        endAxisValue: span.endAxisValue,
        label: a.isForecast ? `⚠ ${a.label}` : a.label,
        color:
          a.severity === "severe"
            ? theme.palette.error.main
            : theme.palette.warning.main,
      });
    }
    return out;
  }, [data, anomalies, showWeatherAnomalies, theme]);

  // Popover state for viewing/adding an event on a clicked bucket.
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeAxisValue, setActiveAxisValue] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftCategory, setDraftCategory] = useState<ClientEventCategory>("other");

  // Resolve the ISO date to attach a new event to: the clicked bucket's
  // period_start (bucket may span a week/month), else the axis value itself.
  const resolveBucketDate = (axisValue: string): string => {
    const rows = data as Array<Record<string, unknown>>;
    const row = rows.find(
      (r) => String(r.label ?? r.period ?? "") === axisValue,
    );
    const start = row?.period_start ? String(row.period_start).slice(0, 10) : null;
    return start ?? axisValue.slice(0, 10);
  };

  const handleAxisClick = (axisValue: string) => {
    if (!clientId) return; // no single client to annotate
    setActiveAxisValue(axisValue);
    setDraftTitle("");
    setDraftCategory("other");
    // Anchor the popover to the chart container.
    const container = document.getElementById(`annotatable-${chartKey}`);
    setAnchorEl(container);
  };

  const closePopover = () => {
    setAnchorEl(null);
    setActiveAxisValue(null);
  };

  const eventsForActiveBucket = useMemo(() => {
    if (!activeAxisValue) return [];
    const bucketDate = resolveBucketDate(activeAxisValue);
    const rows = data as Array<Record<string, unknown>>;
    const row = rows.find(
      (r) => String(r.label ?? r.period ?? "") === activeAxisValue,
    );
    const start = row?.period_start ? String(row.period_start).slice(0, 10) : bucketDate;
    const end = row?.period_end ? String(row.period_end).slice(0, 10) : bucketDate;
    return events.filter((e) => e.eventDate >= start && e.eventDate <= end);
  }, [activeAxisValue, data, events]);

  const handleSave = () => {
    if (!clientId || !activeAxisValue || !draftTitle.trim()) return;
    createEvent.mutate(
      {
        client_id: clientId,
        event_date: resolveBucketDate(activeAxisValue),
        title: draftTitle.trim(),
        category: draftCategory,
      },
      { onSuccess: () => setDraftTitle("") },
    );
  };

  return (
    <Box id={`annotatable-${chartKey}`} sx={{ position: "relative" }}>
      <LineChart
        {...lineChartProps}
        data={data}
        events={markers}
        bands={bands}
        onDateClick={clientId ? handleAxisClick : undefined}
      />

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        slotProps={{
          paper: {
            elevation: 8,
            sx: {
              width: 340,
              borderRadius: 3,
              overflow: "hidden",
              border: `1px solid ${theme.palette.divider}`,
            },
          },
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2.5,
            py: 1.75,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <EventNoteIcon fontSize="small" sx={{ color: "primary.main" }} />
            <Box>
              <Typography variant="caption" color="text.secondary" lineHeight={1.2}>
                {canEdit ? "Add an event" : "Events"}
              </Typography>
              <Typography variant="subtitle2" fontWeight={800} lineHeight={1.2}>
                {activeAxisValue}
              </Typography>
            </Box>
          </Stack>
          <IconButton size="small" onClick={closePopover} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Box sx={{ p: 2.5 }}>
          {eventsForActiveBucket.length > 0 && (
            <Stack spacing={1.25} sx={{ mb: 2.5 }}>
              {eventsForActiveBucket.map((e) => (
                <Stack
                  key={e.id}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: alpha(CATEGORY_COLORS[e.category], 0.1),
                    borderLeft: `3px solid ${CATEGORY_COLORS[e.category]}`,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      noWrap
                      title={`${e.eventDate} — ${e.title}`}
                    >
                      {e.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: CATEGORY_COLORS[e.category], fontWeight: 700 }}
                    >
                      {CATEGORY_LABELS[e.category]}
                    </Typography>
                  </Box>
                  {canEdit && (
                    <IconButton
                      size="small"
                      aria-label="Delete event"
                      onClick={() => deleteEvent.mutate(e.id)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              ))}
            </Stack>
          )}

          {canEdit ? (
            <Stack spacing={2}>
              {eventsForActiveBucket.length > 0 && <Divider>Add another</Divider>}
              <TextField
                label="Event title"
                placeholder="e.g. Heatwave, site incident…"
                value={draftTitle}
                onChange={(e) => setDraftTitle(e.target.value)}
                fullWidth
                autoFocus
              />
              <TextField
                select
                label="Category"
                value={draftCategory}
                onChange={(e) =>
                  setDraftCategory(e.target.value as ClientEventCategory)
                }
                fullWidth
              >
                {(
                  ["weather", "operational", "peak4_admin_notice", "other"] as ClientEventCategory[]
                ).map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {cat === "peak4_admin_notice" ? (
                        <FavoriteIcon sx={{ fontSize: 12, color: CATEGORY_COLORS[cat] }} />
                      ) : (
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            bgcolor: CATEGORY_COLORS[cat],
                          }}
                        />
                      )}
                      <span>{CATEGORY_LABELS[cat]}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<AddIcon />}
                onClick={handleSave}
                disabled={!draftTitle.trim() || createEvent.isPending}
                sx={{ fontWeight: 700, borderRadius: 2 }}
              >
                {createEvent.isPending ? "Adding…" : "Add event"}
              </Button>
            </Stack>
          ) : (
            eventsForActiveBucket.length === 0 && (
              <Stack spacing={1} alignItems="center" sx={{ py: 1.5 }}>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  No events recorded for this date.
                </Typography>
                <Typography variant="caption" color="text.disabled" textAlign="center">
                  You do not have permission to add one.
                </Typography>
              </Stack>
            )
          )}
        </Box>
      </Popover>
    </Box>
  );
};
